/**
 * FMSidebar — Modern Library panel for the Fields Manager.
 *
 * Two states:
 *   - Collapsed (56px): vertical icon rail with quick actions and field types
 *   - Expanded  (300px): brand header, search, action rows, section blocks,
 *                        and a clean field-type grid
 *
 * Field types and section types remain DnD-draggable (preserves the
 * existing canvas drop logic).
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalized } from '../../i18n/getLocalized';
import { useSelector } from 'react-redux';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import {
  Table2, LayoutList, Wand2, Eye,
  PanelLeftClose, PanelLeftOpen, Search, X,
  Sparkles, Command, FileText,
} from 'lucide-react';
import { FIELD_TYPES } from './constants';
import { fieldTypeLabel } from './utils';

// ─── Draggable wrapper ───────────────────────────────────────────────────────
function Draggable({ id, children, className }) {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id,
    data: { fromSidebar: true },
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={className}
      style={{ opacity: isDragging ? 0.4 : 1, cursor: 'grab' }}
    >
      {children}
    </div>
  );
}

// ─── Field type tile ─────────────────────────────────────────────────────────
function FieldTypeTile({ item, t }) {
  const Icon = item.icon;
  const label = fieldTypeLabel(t, item);
  return (
    <div
      className="fmx-field-tile"
      style={{ '--ft-color': item.color, '--ft-bg': item.bg }}
      title={t('fieldsManager.sidebar.dragIntoSection', { label })}
    >
      <span className="fmx-field-tile__icon"><Icon /></span>
      <span className="fmx-field-tile__label">{label}</span>
    </div>
  );
}

// ─── Section type card ───────────────────────────────────────────────────────
function SectionTypeCard({ type, t }) {
  const isTable = type === 'table';
  const Icon = isTable ? Table2 : LayoutList;
  return (
    <div className={`fmx-section-card ${isTable ? 'fmx-section-card--table' : 'fmx-section-card--fields'}`}>
      <span className="fmx-section-card__icon"><Icon /></span>
      <span className="fmx-section-card__body">
        <span className="fmx-section-card__title">
          {isTable
            ? t('fieldsManager.sidebar.tableSection')
            : t('fieldsManager.sidebar.fieldsSection')}
        </span>
        <span className="fmx-section-card__hint">
          {isTable
            ? t('fieldsManager.sidebar.tableHint')
            : t('fieldsManager.sidebar.fieldsHint')}
        </span>
      </span>
    </div>
  );
}

// ─── Brand block ─────────────────────────────────────────────────────────────
function Brand({ logoUrl, companyName, t }) {
  return (
    <div className="fmx-sidebar__brand">
      {logoUrl ? (
        <img src={logoUrl} alt={companyName} className="fmx-sidebar__brand-logo" />
      ) : (
        <span className="fmx-sidebar__brand-mono"><FileText /></span>
      )}
      <div className="fmx-sidebar__brand-text">
        <div className="fmx-sidebar__brand-name">{companyName}</div>
        <div className="fmx-sidebar__brand-sub">{t('fieldsManager.sidebar.title')}</div>
      </div>
    </div>
  );
}

// ─── Main Sidebar ────────────────────────────────────────────────────────────
export default function FMSidebar({
  collapsed,
  onToggleCollapse,
  onShowTemplatePicker,
  onShowPreview,
  onAddFieldToFirstSection,
  onAskAI,
}) {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');

  const { branding } = useSelector((state) => state.settings);
  const logoUrl = branding?.logoUrl || '';
  // companyName may be a multilang object ({en,tr}); resolve to a string so it
  // renders (React error #31 otherwise). No hardcoded brand fallback.
  const companyName = getLocalized(branding?.companyName, i18n.language);

  const sectionIds = ['sidebar-section-fields', 'sidebar-section-table'];
  const fieldIds = FIELD_TYPES.map((ft) => `sidebar-field-${ft.type}`);

  // Match on the localized label as well as the English one, so searching in
  // Turkish ("tarih") finds the Date tile.
  const filteredFields = useMemo(() => {
    if (!query.trim()) return FIELD_TYPES;
    const q = query.trim().toLowerCase();
    return FIELD_TYPES.filter((f) => (
      f.label.toLowerCase().includes(q)
      || fieldTypeLabel(t, f).toLowerCase().includes(q)
      || f.type.includes(q)
    ));
  }, [query, t]);

  // ── Collapsed: icon rail ──────────────────────────────────────────────────
  if (collapsed) {
    return (
      <aside className="fmx-sidebar fmx-sidebar--collapsed" aria-label={t('fieldsManager.sidebar.title')}>
        <button
          className="fmx-sidebar__rail-toggle"
          onClick={onToggleCollapse}
          title={t('fieldsManager.sidebar.expandSidebar')}
        >
          <PanelLeftOpen />
        </button>

        <div className="fmx-sidebar__rail-group">
          <button className="fmx-rail-btn" onClick={onAskAI} title={t('fieldsManager.sidebar.askAI')}>
            <Sparkles />
          </button>
          <button className="fmx-rail-btn" onClick={onShowTemplatePicker} title={t('fieldsManager.templates')}>
            <Wand2 />
          </button>
          <button className="fmx-rail-btn" onClick={onShowPreview} title={t('fieldsManager.preview')}>
            <Eye />
          </button>
        </div>

        <div className="fmx-sidebar__rail-divider" aria-hidden="true" />

        <div className="fmx-sidebar__rail-group">
          {FIELD_TYPES.map((ft) => {
            const Icon = ft.icon;
            return (
              <button
                key={ft.type}
                className="fmx-rail-btn"
                onClick={() => onAddFieldToFirstSection(ft.type)}
                title={t('fieldsManager.sidebar.addFieldOfType', { label: fieldTypeLabel(t, ft) })}
                style={{ '--rail-color': ft.color }}
              >
                <Icon />
              </button>
            );
          })}
        </div>
      </aside>
    );
  }

  // ── Expanded: full panel ──────────────────────────────────────────────────
  return (
    <aside className="fmx-sidebar" aria-label={t('fieldsManager.sidebar.title')}>
      {/* Brand header */}
      <div className="fmx-sidebar__head">
        <Brand logoUrl={logoUrl} companyName={companyName} t={t} />
        <button
          className="fmx-iconbtn fmx-iconbtn--ghost"
          onClick={onToggleCollapse}
          title={t('fieldsManager.sidebar.collapseSidebar')}
        >
          <PanelLeftClose />
        </button>
      </div>

      <div className="fmx-sidebar__body">
        {/* Quick actions */}
        <div className="fmx-sidebar__quick">
          <button className="fmx-quick fmx-quick--ai" onClick={onAskAI}>
            <span className="fmx-quick__icon"><Sparkles /></span>
            <span className="fmx-quick__body">
              <strong>{t('fieldsManager.sidebar.askAI')}</strong>
              <em>{t('fieldsManager.sidebar.askAIDesc')}</em>
            </span>
          </button>
          <div className="fmx-sidebar__quick-row">
            <button className="fmx-quick fmx-quick--sm" onClick={onShowTemplatePicker}>
              <Wand2 />
              <span>{t('fieldsManager.templates')}</span>
            </button>
            <button className="fmx-quick fmx-quick--sm" onClick={onShowPreview}>
              <Eye />
              <span>{t('fieldsManager.preview')}</span>
            </button>
          </div>
        </div>

        <div className="fmx-sidebar__section-divider" aria-hidden="true">
          <span>{t('fieldsManager.sidebar.addToCanvas')}</span>
        </div>

        {/* Section types */}
        <div className="fmx-sidebar__group">
          <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
            <Draggable id="sidebar-section-fields">
              <SectionTypeCard type="fields" t={t} />
            </Draggable>
            <Draggable id="sidebar-section-table">
              <SectionTypeCard type="table" t={t} />
            </Draggable>
          </SortableContext>
        </div>

        <div className="fmx-sidebar__section-divider" aria-hidden="true">
          <span>{t('fieldsManager.sidebar.fieldLibrary')}</span>
        </div>

        {/* Search */}
        <div className="fmx-sidebar__search">
          <Search className="fmx-sidebar__search-icon" />
          <input
            type="text"
            placeholder={t('fieldsManager.sidebar.searchFieldTypes')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              type="button"
              className="fmx-sidebar__search-clear"
              onClick={() => setQuery('')}
              title={t('fieldsManager.sidebar.clearSearch')}
            >
              <X />
            </button>
          )}
        </div>

        {/* Field types grid */}
        <div className="fmx-field-grid">
          <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
            {filteredFields.map((ft) => (
              <Draggable key={ft.type} id={`sidebar-field-${ft.type}`}>
                <FieldTypeTile item={ft} t={t} />
              </Draggable>
            ))}
          </SortableContext>
          {filteredFields.length === 0 && (
            <div className="fmx-field-grid__empty">
              {t('fieldsManager.sidebar.noFieldTypes', { query })}
            </div>
          )}
        </div>
      </div>

      {/* Footer hint */}
      <div className="fmx-sidebar__foot">
        <span className="fmx-sidebar__foot-icon"><Command /></span>
        <span>
          {t('fieldsManager.sidebar.footHintBefore')}
          <kbd>⌘K</kbd>
          {t('fieldsManager.sidebar.footHintAfter')}
        </span>
      </div>
    </aside>
  );
}
